import { useEffect, useRef, useState } from 'react';
import type { StreamEvent } from '@hypevault/shared-types';

/**
 * Marathon (Minimal) — single-cell countdown that briefly cross-fades to
 * "+N" with contributor on every contribution, then back to the timer.
 *
 * Idle:   ┌─── 02:31:45 ───┐
 * Reveal: ┌──── +30s ──────┐    (timer faded out, contributor below in tiny caps)
 *                                  → fades back to the timer after holdSeconds
 *
 * No docking, no slide — both states render absolutely positioned in the
 * same container; opacity transitions handle the swap. Same gradient/poll/
 * marathon-reveal pipeline as Marathon (Compact), so the existing
 * /api/marathon/test-reveal endpoint and its dashboard tester drive both
 * widgets out of the box.
 *
 * Config (all optional):
 *   gradient: 'sunset'|'aurora'|'fire'|'ocean'|'candy'   (default 'sunset')
 *   holdSeconds: number                                  (default 2.5)
 *   fontSize: number                                     (default 36)
 *   showContributor: boolean                             (default true)
 */

interface MarathonSnapshot {
  isActive: boolean;
  name: string | null;
  endsAt: string | null;
  pausedAt: string | null;
  startedAt: string | null;
  initialDurationSec: number | null;
  recent: Array<{
    contributorName: string | null;
    eventType: string;
    secondsAdded: number;
    createdAt: string;
  }>;
}

interface RevealItem {
  key: string;
  contributorName: string;
  eventType: string;
  secondsAdded: number;
}

interface TestReveal {
  id: string;
  eventType: string;
  contributorName: string;
  secondsAdded: number;
  createdAt: string;
}

const GRADIENTS: Record<string, string> = {
  sunset: 'linear-gradient(135deg, #f655a6 0%, #ff8a4c 50%, #ffcc33 100%)',
  aurora: 'linear-gradient(135deg, #22d3ee 0%, #a855f7 50%, #f655a6 100%)',
  fire: 'linear-gradient(135deg, #ef4444 0%, #f97316 50%, #facc15 100%)',
  ocean: 'linear-gradient(135deg, #14b8a6 0%, #3b82f6 50%, #8b5cf6 100%)',
  candy: 'linear-gradient(135deg, #f655a6 0%, #c084fc 50%, #60a5fa 100%)',
};

const EVENT_LABEL: Record<string, string> = {
  follow: 'FOLLOW',
  kick_follow: 'KICK FOLLOW',
  sub_t1: 'SUB',
  sub_t2: 'TIER 2 SUB',
  sub_t3: 'TIER 3 SUB',
  sub_prime: 'PRIME SUB',
  yt_member: 'MEMBER',
  kick_sub: 'KICK SUB',
  kick_gift_sub: 'GIFT KICK SUB',
  cheer: 'CHEER',
  kick_kicks: 'KICKS',
  raid: 'RAID',
  yt_superchat: 'SUPERCHAT',
  yt_supersticker: 'STICKER',
  tip: 'TIP',
  hypevault_card_redemption: 'CARD',
  hypevault_card_purchase: 'PURCHASE',
  manual_adjust: 'HOST',
};

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatAdded(seconds: number): string {
  if (seconds < 60) return `+${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `+${m}m` : `+${m}m ${s}s`;
}

export function MarathonMinimalWidget({
  fields,
  token,
  latestEvent,
  testReveal,
}: {
  fields: Record<string, unknown>;
  token: string;
  latestEvent: StreamEvent | null;
  testReveal?: TestReveal | null;
}) {
  const gradient =
    GRADIENTS[(fields.gradient as string | undefined) ?? 'sunset'] ?? GRADIENTS.sunset!;
  const holdMs = Math.max(1000, Number(fields.holdSeconds ?? 2.5) * 1000);
  const fontSize = Number(fields.fontSize ?? 36);
  const showContributor = (fields.showContributor as boolean | undefined) ?? true;

  const [snapshot, setSnapshot] = useState<MarathonSnapshot | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [reveal, setReveal] = useState<RevealItem | null>(null);
  const [queue, setQueue] = useState<RevealItem[]>([]);

  const lastSeenCreatedAtRef = useRef<string | null>(null);
  const lastEventIdRef = useRef<string | null>(null);
  const lastTestRevealIdRef = useRef<string | null>(null);

  // ───── Snapshot fetch + diff (2s poll) ─────
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = () => {
      fetch(`/api/marathon/public?token=${encodeURIComponent(token)}`, { cache: 'no-store' })
        .then((r) => (r.ok ? (r.json() as Promise<MarathonSnapshot>) : null))
        .then((data) => {
          if (cancelled || !data) return;
          setSnapshot(data);
          if (!data.recent || data.recent.length === 0) return;
          const newest = data.recent[0]!.createdAt;
          if (lastSeenCreatedAtRef.current === null) {
            lastSeenCreatedAtRef.current = newest;
            return;
          }
          const lastSeen = lastSeenCreatedAtRef.current;
          const fresh = data.recent
            .filter((r) => r.createdAt > lastSeen && r.secondsAdded > 0)
            .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
          if (fresh.length === 0) return;
          const items: RevealItem[] = fresh.map((r) => ({
            key: r.createdAt,
            contributorName: r.contributorName ?? 'Someone',
            eventType: r.eventType,
            secondsAdded: r.secondsAdded,
          }));
          setQueue((q) => [...q, ...items]);
          lastSeenCreatedAtRef.current = newest;
        })
        .catch(() => undefined);
    };
    load();
    const interval = setInterval(load, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [token]);

  // ───── Refetch immediately on every StreamEvent ─────
  useEffect(() => {
    if (!latestEvent || !token) return;
    if (latestEvent.id === lastEventIdRef.current) return;
    lastEventIdRef.current = latestEvent.id;
    const t = setTimeout(() => {
      fetch(`/api/marathon/public?token=${encodeURIComponent(token)}`, { cache: 'no-store' })
        .then((r) => (r.ok ? (r.json() as Promise<MarathonSnapshot>) : null))
        .then((data) => {
          if (!data) return;
          setSnapshot(data);
          if (!data.recent || data.recent.length === 0) return;
          const lastSeen = lastSeenCreatedAtRef.current;
          if (lastSeen === null) {
            lastSeenCreatedAtRef.current = data.recent[0]!.createdAt;
            return;
          }
          const fresh = data.recent
            .filter((r) => r.createdAt > lastSeen && r.secondsAdded > 0)
            .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
          if (fresh.length === 0) return;
          const items: RevealItem[] = fresh.map((r) => ({
            key: r.createdAt,
            contributorName: r.contributorName ?? 'Someone',
            eventType: r.eventType,
            secondsAdded: r.secondsAdded,
          }));
          setQueue((q) => [...q, ...items]);
          lastSeenCreatedAtRef.current = data.recent[0]!.createdAt;
        })
        .catch(() => undefined);
    }, 300);
    return () => clearTimeout(t);
  }, [latestEvent, token]);

  // ───── Test reveals from /api/marathon/test-reveal ─────
  useEffect(() => {
    if (!testReveal) return;
    if (testReveal.id === lastTestRevealIdRef.current) return;
    lastTestRevealIdRef.current = testReveal.id;
    setQueue((q) => [
      ...q,
      {
        key: testReveal.id,
        contributorName: testReveal.contributorName,
        eventType: testReveal.eventType,
        secondsAdded: testReveal.secondsAdded,
      },
    ]);
  }, [testReveal]);

  // ───── Local tick (countdown smooths between polls) ─────
  useEffect(() => {
    if (!snapshot) return;
    const tick = () => {
      if (!snapshot.endsAt) {
        setRemaining(0);
        return;
      }
      const endsAt = new Date(snapshot.endsAt).getTime();
      const ref = snapshot.pausedAt ? new Date(snapshot.pausedAt).getTime() : Date.now();
      setRemaining(Math.max(0, Math.floor((endsAt - ref) / 1000)));
    };
    tick();
    const interval = setInterval(tick, 500);
    return () => clearInterval(interval);
  }, [snapshot]);

  // ───── Drain — pure state hop when no reveal is showing ─────
  useEffect(() => {
    if (reveal) return;
    if (queue.length === 0) return;
    const next = queue[0]!;
    setQueue((q) => q.slice(1));
    setReveal(next);
  }, [reveal, queue]);

  // ───── Hold timer — clears reveal after holdMs, drain picks up the next ─────
  useEffect(() => {
    if (!reveal) return;
    const t = setTimeout(() => setReveal(null), holdMs);
    return () => clearTimeout(t);
  }, [reveal, holdMs]);

  const hasActiveMarathon = snapshot?.isActive === true;
  // Mount whenever there's an active marathon OR a queued/showing reveal
  // (so the dashboard tester can preview without starting a marathon).
  if (!hasActiveMarathon && !reveal && queue.length === 0) return null;
  const isPaused = snapshot?.pausedAt != null;

  const sharedDigitStyle: React.CSSProperties = {
    fontWeight: 900,
    fontSize,
    letterSpacing: '-0.02em',
    fontVariantNumeric: 'tabular-nums',
    color: '#fff',
    lineHeight: 1,
    textShadow: '0 1px 2px rgba(0,0,0,0.35), 0 0 18px rgba(255,255,255,0.18)',
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 8,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <div
        style={{
          position: 'relative',
          background: gradient,
          borderRadius: 14,
          padding: '10px 22px',
          minWidth: 'min-content',
          boxShadow: '0 12px 36px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)',
          overflow: 'hidden',
          opacity: isPaused && !reveal ? 0.75 : 1,
          transition: 'opacity 250ms ease',
        }}
      >
        {/* Top sheen for that glossy look */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '50%',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.18), transparent)',
            pointerEvents: 'none',
          }}
        />

        {/* Both layers stacked — opacity crossfade based on `reveal`. */}
        <div style={{ position: 'relative', display: 'grid' }}>
          {/* Timer layer */}
          <div
            style={{
              gridArea: '1 / 1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              opacity: reveal ? 0 : 1,
              transform: reveal ? 'scale(0.94)' : 'scale(1)',
              transition: 'opacity 220ms ease, transform 280ms cubic-bezier(0.16, 1, 0.3, 1)',
              pointerEvents: reveal ? 'none' : 'auto',
            }}
          >
            {isPaused && (
              <span
                aria-hidden
                style={{
                  display: 'inline-block',
                  fontSize: fontSize * 0.55,
                  lineHeight: 1,
                  fontWeight: 900,
                  color: 'rgba(255,255,255,0.85)',
                }}
              >
                ‖
              </span>
            )}
            <span style={sharedDigitStyle}>{formatTime(remaining)}</span>
          </div>

          {/* Reveal layer */}
          <div
            style={{
              gridArea: '1 / 1',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: showContributor ? 2 : 0,
              opacity: reveal ? 1 : 0,
              transform: reveal ? 'scale(1)' : 'scale(1.08)',
              transition: 'opacity 240ms ease, transform 280ms cubic-bezier(0.16, 1, 0.3, 1)',
              pointerEvents: reveal ? 'auto' : 'none',
            }}
          >
            <span style={sharedDigitStyle}>{reveal ? formatAdded(reveal.secondsAdded) : ''}</span>
            {showContributor && reveal && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 1.2,
                  color: 'rgba(255,255,255,0.85)',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  whiteSpace: 'nowrap',
                  maxWidth: 280,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block',
                }}
              >
                {reveal.contributorName} ·{' '}
                {EVENT_LABEL[reveal.eventType] ?? reveal.eventType.toUpperCase()}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
